import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { cajaService } from '@/services/caja.service';
import { ventasService } from '@/services/ventas.service';
import { Loader2, Plus, Minus, DollarSign, Clock, Check, X, ChevronDown, ChevronUp, Trash2, Receipt, Eye } from 'lucide-react';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { ZReportTicket } from '@/components/ZReportTicket';
import { Printer } from 'lucide-react';

export function CashRegisterPage() {
  const { user } = useAuthStore();
  const [openRegisters, setOpenRegisters] = useState<any[]>([]);
  const [register, setRegister] = useState<any>(null); // used for close modal
  const [loading, setLoading] = useState(true);
  const [showOpenForm, setShowOpenForm] = useState(false);
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [registerSales, setRegisterSales] = useState<any[]>([]);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [salesSearch, setSalesSearch] = useState('');
  const [salesPage, setSalesPage] = useState(1);
  const [salesTotalPages, setSalesTotalPages] = useState(1);
  const [loadingSales, setLoadingSales] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [initialCash, setInitialCash] = useState('500');
  const [openNote, setOpenNote] = useState('');
  const [countedCash, setCountedCash] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [openAmountError, setOpenAmountError] = useState('');
  const [previewRegister, setPreviewRegister] = useState<any>(null);
  const [showZPreview, setShowZPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [supervisorUnclosedSession, setSupervisorUnclosedSession] = useState<any>(null);
  const [supervisorPassword, setSupervisorPassword] = useState('');
  const [supervisorError, setSupervisorError] = useState('');
  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();
  const [printRegister, setPrintRegister] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);
  
  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  const triggerPrint = async (h: any) => {
    try {
      // Fetch full register to get sales and payments for breakdown
      const fullRegister = await cajaService.findById(h.id);
      setPrintRegister(fullRegister);
      setTimeout(() => handlePrint(), 100);
    } catch (err: any) {
      handleApiError(err);
    }
  };

  const loadStatus = useCallback(async () => {
    if (!user?.branch?.id) return;
    setLoading(true);
    try {
      // Check status to detect if an unclosed session from yesterday exists
      const statusRes = await cajaService.getStatus(user.branch.id);
      if (statusRes?.requiresSupervisorClose && statusRes?.unclosedSession) {
        setSupervisorUnclosedSession(statusRes.unclosedSession);
      } else {
        setSupervisorUnclosedSession(null);
      }

      let openList: any[] = [];
      const openListRes = await cajaService.getOpenRegisters(user.branch.id);
      if (Array.isArray(openListRes)) openList = openListRes;
      else if (openListRes?.data && Array.isArray(openListRes.data)) openList = openListRes.data;
      else if (openListRes?.value && Array.isArray(openListRes.value)) openList = openListRes.value;

      setOpenRegisters(openList);

      const hist = await cajaService.history(user.branch.id, 1, 10);
      let histData: any[] = [];
      if (Array.isArray(hist)) histData = hist;
      else if (hist?.data && Array.isArray(hist.data)) histData = hist.data;
      else if (hist?.value && Array.isArray(hist.value)) histData = hist.value;

      const openIds = new Set(openList.map((r: any) => r.id));
      const filtered = histData.filter((h: any) => !openIds.has(h.id));
      setHistory(filtered);
    } catch (err: any) { handleApiError(err); }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadRegisterSales = async (registerId: string, page = 1, search = '') => {
    setLoadingSales(true);
    try {
      const result = await ventasService.findAll({
        companyId: user!.company!.id,
        status: 'COMPLETED',
        limit: 20,
        page,
        search: search || undefined,
      });
      const sales = (result.data || []).filter((s: any) => s.cashRegisterId === registerId);
      setRegisterSales(sales);
      setSalesTotalPages(result.totalPages || 1);
    } catch { /* ignore */ }
    setLoadingSales(false);
  };

  const handleCancelSale = async (sale: any) => {
    const confirmed = await dialogs.confirm(
      `¿Seguro que deseas cancelar el ticket ${sale.folio}? Esta acción no se puede deshacer.`,
      { title: 'Cancelar venta', confirmText: 'Sí, Cancelar', cancelText: 'No' }
    );
    if (!confirmed) return;
    try {
      setCancellingId(sale.id);
      await ventasService.cancel(sale.id, {
        cancelledById: user!.id,
        reason: 'Cancelado por administrador desde caja',
      });
      await loadRegisterSales(register.id);
      await loadStatus();
    } catch (err: any) {
      await dialogs.alert(err?.response?.data?.message || 'Error al cancelar', 'Error');
    } finally {
      setCancellingId(null);
    }
  };

  const toggleSales = (reg: any) => {
    setRegister(reg); // set which register we're viewing
    setSalesPage(1);
    setSalesSearch('');
    loadRegisterSales(reg.id, 1, '');
    setShowSalesModal(true);
  };

  const handleOpen = async () => {
    if (!user?.branch?.id || !user?.id) return;
    const amount = parseFloat(initialCash);
    if (isNaN(amount) || amount < 0) {
      setOpenAmountError('Ingresa un monto válido');
      return;
    }
    setSaving(true);
    try {
      await cajaService.open({
        branchId: user.branch.id,
        openedById: user.id,
        initialCash: amount,
        note: openNote || undefined,
      });
      setShowOpenForm(false);
      setInitialCash('500');
      setOpenNote('');
      loadStatus();
    } catch (err: any) { handleApiError(err); }
    setSaving(false);
  };

  const handleClose = async () => {
    if (!register?.id || !user?.id) return;
    const amount = parseFloat(countedCash);
    if (isNaN(amount) || amount < 0) return;
    setSaving(true);
    try {
      await cajaService.close(register.id, {
        closedById: user.id,
        countedCash: amount,
        note: closeNote || undefined,
      });
      setShowCloseForm(false);
      setShowZPreview(false);
      setPreviewRegister(null);
      setCountedCash('');
      setCloseNote('');
      loadStatus();
    } catch (err: any) { handleApiError(err); }
    setSaving(false);
  };

  const formatCurrency = (val: any) => `$${Number(val || 0).toFixed(2)}`;
  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 size={24} className="animate-spin text-ios-gray" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadStatus} onLogin={handleAuthRedirect} />
      {!error && (
      <>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Caja</h1>
        <p className="text-sm text-ios-gray">Vista del administrador</p>
      </div>

      {/* Supervisor Warning for Unclosed Session from Previous Day */}
      {supervisorUnclosedSession && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-xl shadow-sm mb-4 animate-in slide-in-from-top-2">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-bold text-amber-900 text-sm flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-amber-600" />
                Atención Administrador: Caja del día anterior sin cerrar
              </h3>
              <p className="text-xs text-amber-800 mt-1">
                La sesión de caja iniciada el <span className="font-semibold">{supervisorUnclosedSession.openedDate}</span> no fue cerrada por el cajero. Se requiere auditoría y autorización para realizar el cierre antes de continuar.
              </p>
            </div>
            <button
              onClick={() => {
                setRegister(supervisorUnclosedSession);
                setCountedCash(String(supervisorUnclosedSession.currentCash || 0));
                setShowCloseForm(true);
              }}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-all"
            >
              Auditar y Cerrar Caja de Ayer
            </button>
          </div>
        </div>
      )}

      {/* Open Registers */}
      {openRegisters.length === 0 ? (
        <div className="ios-card p-6 text-center">
          <div className="w-12 h-12 bg-ios-gray-6 rounded-full flex items-center justify-center mx-auto mb-3">
            <DollarSign size={24} className="text-ios-gray" />
          </div>
          <p className="font-medium">No hay cajas abiertas</p>
          <p className="text-sm text-ios-gray mt-1">Ningún cajero ha abierto su caja en esta sucursal</p>
        </div>
      ) : (
        <div className="space-y-4">
          {openRegisters.map((reg: any) => (
            <div key={reg.id} className="ios-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-ios-green rounded-full animate-pulse" />
                  <span className="font-semibold text-ios-green">Caja Abierta</span>
                  <span className="text-xs text-ios-gray">Desde {formatDate(reg.openedAt)}</span>
                </div>
                <button
                  onClick={async () => { 
                    setRegister(reg); 
                    setCountedCash(String(reg.expectedCash || 0)); 
                    setShowCloseForm(true);
                    setShowZPreview(false);
                    setLoadingPreview(true);
                    try {
                      const full = await cajaService.findById(reg.id);
                      setPreviewRegister(full);
                    } catch { 
                      setPreviewRegister(null); 
                    }
                    setLoadingPreview(false);
                  }}
                  className="px-4 py-2 rounded-[10px] text-sm bg-ios-red text-white hover:bg-red-600 font-medium flex items-center gap-2"
                >
                  <Minus size={16} /> Cerrar Turno / Corte Z
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div className="bg-ios-gray-6 rounded-[12px] p-3">
                  <p className="text-[10px] text-ios-gray uppercase">Cajero</p>
                  <p className="text-sm font-bold mt-1">{reg.openedBy?.fullName}</p>
                </div>
                <div className="bg-ios-gray-6 rounded-[12px] p-3">
                  <p className="text-[10px] text-ios-gray uppercase">Fondo inicial</p>
                  <p className="text-lg font-bold mt-1">{formatCurrency(reg.initialCash)}</p>
                </div>
                <div className="bg-ios-gray-6 rounded-[12px] p-3">
                  <p className="text-[10px] text-ios-gray uppercase">Ventas</p>
                  <p className="text-lg font-bold mt-1 text-ios-blue">{formatCurrency(reg.totalSales)}</p>
                </div>
                <div className="bg-ios-gray-6 rounded-[12px] p-3">
                  <p className="text-[10px] text-ios-gray uppercase">Esperado</p>
                  <p className="text-lg font-bold mt-1">{formatCurrency(reg.expectedCash)}</p>
                </div>
              </div>

              <div className="border-t border-ios-gray-5 pt-3">
                <button
                  onClick={() => toggleSales(reg)}
                  className="flex items-center gap-2 text-sm font-medium text-ios-blue hover:text-blue-700 transition-colors"
                >
                  <Receipt size={16} />
                  Ver ventas de esta sesión ({reg.saleCount || 0})
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div>
          <h2 className="font-semibold mb-3">Cortes anteriores</h2>
          <div className="space-y-2">
            {history.map((h: any) => (
              <div key={h.id} className="ios-card p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${h.status === 'CLOSED' ? 'bg-ios-green/10' : 'bg-yellow-50'}`}>
                    {h.status === 'CLOSED' ? <Check size={16} className="text-ios-green" /> : <Clock size={16} className="text-yellow-500" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{formatDate(h.openedAt)}</p>
                    <p className="text-xs text-ios-gray">
                      {h.openedBy?.fullName} · {h._count?.sales || 0} ventas
                    </p>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <div>
                    <p className="text-sm font-semibold">{formatCurrency(h.totalSales)}</p>
                    {h.difference !== null && (
                      <p className={`text-xs ${Number(h.difference) >= 0 ? 'text-ios-green' : 'text-ios-red'}`}>
                        Dif: {formatCurrency(h.difference)}
                      </p>
                    )}
                  </div>
                  {h.status === 'CLOSED' && (
                    <button 
                      onClick={() => triggerPrint(h)} 
                      className="text-xs flex items-center gap-1 text-ios-blue hover:text-blue-700 bg-blue-50 px-2 py-1 rounded"
                    >
                      <Printer size={12} /> Reimprimir Corte Z
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open Register Modal */}
      {showOpenForm && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-sm shadow-ios-xl">
            <div className="p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">Abrir Caja</h3>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Monto inicial *</label>
                <input
                  type="number"
                  value={initialCash}
                  onChange={(e) => { setInitialCash(e.target.value); setOpenAmountError(''); }}
                  placeholder="0.00"
                  className="ios-input w-full text-lg font-semibold"
                  step="0.01"
                  autoFocus
                />
                {openAmountError && <p className="text-xs text-ios-red mt-1">{openAmountError}</p>}
              </div>
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Nota (opcional)</label>
                <input
                  type="text"
                  value={openNote}
                  onChange={(e) => setOpenNote(e.target.value)}
                  placeholder="Ej: Apertura turno matutino"
                  className="ios-input w-full"
                />
              </div>
            </div>
            <div className="p-4 border-t border-ios-gray-5 flex gap-2">
              <button onClick={() => setShowOpenForm(false)} className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6">
                Cancelar
              </button>
              <button
                onClick={handleOpen}
                disabled={saving || !initialCash}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-green text-white font-medium hover:bg-green-600 disabled:opacity-50"
              >
                {saving ? 'Abriendo...' : 'Abrir Caja'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Register Modal */}
      {showCloseForm && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-sm shadow-ios-xl">
            <div className="p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">Cerrar Caja - Corte</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-ios-gray-6 rounded-[10px] p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-ios-gray">Fondo inicial</span>
                  <span>{formatCurrency(register.initialCash)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ios-gray">Ventas</span>
                  <span className="text-ios-blue">{formatCurrency(register.totalSales)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1 border-t border-ios-gray-5">
                  <span>Esperado</span>
                  <span>{formatCurrency(register.expectedCash)}</span>
                </div>
              </div>

              <div>
                <label className="text-xs text-ios-gray mb-1 block">Efectivo contado *</label>
                <input
                  type="number"
                  value={countedCash}
                  onChange={(e) => setCountedCash(e.target.value)}
                  placeholder="0.00"
                  className="ios-input w-full text-lg font-semibold"
                  step="0.01"
                  autoFocus
                />
                {countedCash && (
                  <p className={`text-xs mt-1 ${Number(countedCash) >= (register.expectedCash || 0) ? 'text-ios-green' : 'text-ios-red'}`}>
                    Diferencia: {formatCurrency(Number(countedCash) - (register.expectedCash || 0))}
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Nota (opcional)</label>
                <input
                  type="text"
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  placeholder="Ej: Cierre turno matutino"
                  className="ios-input w-full"
                />
              </div>

              {/* Vista Previa del Corte Z */}
              <div className="border-t border-ios-gray-5 pt-3">
                <button
                  onClick={() => setShowZPreview(!showZPreview)}
                  className="flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-ios-blue hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  {loadingPreview ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : showZPreview ? (
                    <ChevronUp size={16} />
                  ) : (
                    <Eye size={16} />
                  )}
                  {loadingPreview ? 'Cargando...' : showZPreview ? 'Ocultar Vista Previa' : 'Ver Vista Previa del Corte Z'}
                </button>

                {showZPreview && previewRegister && (
                  <div className="mt-3 bg-white border border-gray-200 rounded-[12px] p-4 text-xs font-mono max-h-64 overflow-y-auto space-y-1.5 shadow-sm">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                      📋 DESGLOSE DEL CORTE
                    </p>
                    
                    {/* Payment breakdown */}
                    {(() => {
                      const breakdown: Record<string, number> = { CASH: 0, CARD: 0, TRANSFER: 0, VOUCHER: 0, CREDIT: 0, NOTA_CREDITO: 0 };
                      previewRegister.sales?.forEach?.((s: any) => {
                        s.payments?.forEach?.((p: any) => {
                          if (breakdown[p.method] !== undefined) breakdown[p.method] += Number(p.amount);
                        });
                      });
                      const methodLabels: Record<string, string> = { CASH: 'Efectivo', CARD: 'Tarjeta', TRANSFER: 'Transferencia', VOUCHER: 'Vales', CREDIT: 'Crédito', NOTA_CREDITO: 'Notas de Crédito' };
                      return Object.entries(breakdown)
                        .filter(([, v]) => v > 0)
                        .map(([method, amount]) => (
                          <div key={method} className="flex justify-between">
                            <span>{methodLabels[method] || method}</span>
                            <span className="font-semibold">${amount.toFixed(2)}</span>
                          </div>
                        ));
                    })()}

                    <div className="border-t border-dashed border-gray-200 my-1.5"></div>

                    <div className="flex justify-between">
                      <span>Ventas realizadas</span>
                      <span className="font-semibold">{previewRegister.sales?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total ingresos</span>
                      <span className="font-semibold text-blue-600">{formatCurrency(previewRegister.totalSales)}</span>
                    </div>

                    {(Number(previewRegister.totalCashIn) > 0 || Number(previewRegister.totalCashOut) > 0) && (
                      <>
                        <div className="border-t border-dashed border-gray-200 my-1.5"></div>
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Movimientos</p>
                        {Number(previewRegister.totalCashIn) > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>+ Ingresos extras</span>
                            <span>+${Number(previewRegister.totalCashIn).toFixed(2)}</span>
                          </div>
                        )}
                        {Number(previewRegister.totalCashOut) > 0 && (
                          <div className="flex justify-between text-red-500">
                            <span>− Retiros</span>
                            <span>-${Number(previewRegister.totalCashOut).toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}

                    <div className="border-t border-dashed border-gray-200 my-1.5"></div>
                    <div className="flex justify-between font-bold text-sm">
                      <span>EFECTIVO ESPERADO</span>
                      <span>{formatCurrency(
                        previewRegister.expectedCash ??
                        (Number(previewRegister.initialCash) + (previewRegister.sales?.reduce((sum: number, s: any) => sum + (s.payments?.filter((p: any) => p.method === 'CASH').reduce((ps: number, p: any) => ps + Number(p.amount), 0) || 0), 0) || 0) + Number(previewRegister.totalCashIn) - Number(previewRegister.totalCashOut))
                      )}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Apertura:</span>
                      <span>{formatCurrency(previewRegister.initialCash)}</span>
                    </div>

                    {previewRegister.note && (
                      <div className="border-t border-dashed border-gray-200 my-1.5 pt-1.5 text-[10px] text-gray-500">
                        <span className="font-bold">Nota:</span> {previewRegister.note}
                      </div>
                    )}
                  </div>
                )}

                {showZPreview && !previewRegister && !loadingPreview && (
                  <p className="text-xs text-gray-400 text-center mt-2">No se pudieron cargar los datos del corte</p>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-ios-gray-5 flex gap-2">
              <button onClick={() => { setShowCloseForm(false); setShowZPreview(false); setPreviewRegister(null); }} className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6">
                Cancelar
              </button>
              <button
                onClick={handleClose}
                disabled={saving || !countedCash}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-red text-white font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {saving ? 'Cerrando...' : 'Cerrar y Corte'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
      
      {/* Sales Modal */}
      {showSalesModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-2xl shadow-ios-xl flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="p-4 border-b border-ios-gray-5 flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Ventas de esta sesión</h3>
                <p className="text-xs text-ios-gray mt-0.5">Abierta por {register?.openedBy?.fullName} • {register?.saleCount || 0} ventas</p>
              </div>
              <button onClick={() => setShowSalesModal(false)} className="p-2 text-ios-gray hover:bg-ios-gray-6 rounded-full">
                <X size={20} />
              </button>
            </div>

            {/* Search */}
            <div className="p-3 border-b border-ios-gray-5">
              <input
                type="text"
                placeholder="Buscar por folio o cliente..."
                value={salesSearch}
                onChange={e => {
                  setSalesSearch(e.target.value);
                  setSalesPage(1);
                  loadRegisterSales(register.id, 1, e.target.value);
                }}
                className="ios-input w-full text-sm"
              />
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loadingSales ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-ios-gray" />
                </div>
              ) : registerSales.length === 0 ? (
                <p className="text-sm text-ios-gray text-center py-8">No hay ventas en esta sesión.</p>
              ) : (
                registerSales.map((sale: any) => (
                  <div key={sale.id} className="flex items-center justify-between bg-ios-gray-6 rounded-[12px] px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold">{sale.folio}</p>
                      <p className="text-xs text-ios-gray">
                        {new Date(sale.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        {' • '}{sale.customer?.name || 'Público en General'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-sm">${Number(sale.grandTotal).toFixed(2)}</span>
                      <button
                        onClick={() => handleCancelSale(sale)}
                        disabled={cancellingId === sale.id}
                        className="p-1.5 text-ios-red hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Cancelar venta"
                      >
                        {cancellingId === sale.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {salesTotalPages > 1 && (
              <div className="p-3 border-t border-ios-gray-5 flex items-center justify-between">
                <button
                  disabled={salesPage <= 1}
                  onClick={() => { const p = salesPage - 1; setSalesPage(p); loadRegisterSales(register.id, p, salesSearch); }}
                  className="px-3 py-1.5 text-sm border border-ios-gray-5 rounded-[8px] disabled:opacity-40 hover:bg-ios-gray-6"
                >
                  Anterior
                </button>
                <span className="text-xs text-ios-gray">Página {salesPage} de {salesTotalPages}</span>
                <button
                  disabled={salesPage >= salesTotalPages}
                  onClick={() => { const p = salesPage + 1; setSalesPage(p); loadRegisterSales(register.id, p, salesSearch); }}
                  className="px-3 py-1.5 text-sm border border-ios-gray-5 rounded-[8px] disabled:opacity-40 hover:bg-ios-gray-6"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden ticket for printing past reports */}
      <ZReportTicket register={printRegister} ref={printRef} />
    </div>
  );
}




